import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { SpinnerAnimatedIcon } from 'secondstep-components'
import { selectors as licenseSelectors } from 'store/licenseManager'
import {
  getCurrentPacingEndDateAndName,
  getPathwayDates,
} from 'store/reportsManager/helpers'
import {
  operations as reportsOperations,
  selectors as reportsSelectors,
} from 'store/reportsManager'
import AdminSortButton from 'components/AdminSortButton'
import EmptyMessage from './EmptyMessage'
import LeftBox from './LeftBox'
import UserProgressRow from 'components/admin/UserProgressRow'

import {
  itemsPerPage,
  notCompletedEmptyText,
  notSetupText,
  LOADING_TEXT,
  PRACTICE_TYPES,
  PROGRAM_KEYS,
  sortOrders,
  studentActivitiesEmptyText,
} from './constants'
import { createSortButtonArray } from './helpers'
import {
  ContentPanelWrapper,
  LoadingWrapper,
  NotSetupWrapper,
  StyledSortableAccordionList,
} from './component.styles'

const UserProgressList = ({
  adminSortKey,
  adminSortOrder,
  currentPacingName,
  error,
  fetchHsUserProgressSnapshot,
  isFetching,
  isOptedInToStudentActivities,
  isSetup,
  lastUpdatedAt,
  pathwayDatesExist,
  practiceTypeData,
  siteId,
  updateHsUserProgressPaginationSortKey,
  updateHsUserProgressPaginationSortOrder,
  userProgressSiteId,
  users,
}) => {
  const [pageViewNumber, setPageViewNumber] = useState(1)
  const [sortKey, setSortKey] = useState(adminSortKey)
  const [sortOrder, setSortOrder] = useState(adminSortOrder)
  const [sortedUsers, setSortedUsers] = useState(users)
  const [userHasCompletedAnActivity, setUserHasCompletedAnActivity] = useState(
    false,
  )

  const programKey = PROGRAM_KEYS.HS
  const { description, practiceType, title } = practiceTypeData || {}

  const handlePageChange = pageNumber => {
    setPageViewNumber(pageNumber)
  }

  const handleSort = newSortKey => {
    if (sortKey === newSortKey) {
      const newSortOrder =
        sortOrder === sortOrders.asc ? sortOrders.desc : sortOrders.asc
      setPageViewNumber(1)
      setSortedUsers(
        reportsSelectors.sortUsersByKey(users, sortKey, newSortOrder),
      )
      setSortOrder(newSortOrder)
      updateHsUserProgressPaginationSortOrder(newSortOrder)
    } else {
      setPageViewNumber(1)
      setSortKey(newSortKey)
      setSortOrder(sortOrders.asc)
      setSortedUsers(
        reportsSelectors.sortUsersByKey(users, newSortKey, sortOrders.asc),
      )
      updateHsUserProgressPaginationSortKey(newSortKey)
      updateHsUserProgressPaginationSortOrder(sortOrders.asc)
    }
  }

  useEffect(() => {
    if ((!users || siteId !== userProgressSiteId) && !error) {
      fetchHsUserProgressSnapshot(siteId, programKey, practiceType)
    }

    if (users) {
      const startIndex = (pageViewNumber - 1) * itemsPerPage
      const lastIndex = pageViewNumber * itemsPerPage
      const pagedUsers = users.slice(startIndex, lastIndex)

      const activityCompleted = users?.find(user => {
        let hasCompletedPractice
        if (practiceType === PRACTICE_TYPES.EDUCATOR_PRACTICE) {
          hasCompletedPractice = user.hasEducatorPractice
        }
        if (practiceType === PRACTICE_TYPES.SCHOOLWIDE_PRACTICE) {
          hasCompletedPractice = user.hasSchoolwidePractice
        }
        if (practiceType === PRACTICE_TYPES.STUDENT_ACTIVITY) {
          hasCompletedPractice = user.hasStudentActivities
        }

        return hasCompletedPractice
      })

      setUserHasCompletedAnActivity(!!activityCompleted)
      setSortedUsers(pagedUsers)
    }
  }, [error, pageViewNumber, userHasCompletedAnActivity, users])

  let emptyText = notCompletedEmptyText
  let showDescription = true
  let showTable = true
  const sortButtons = createSortButtonArray(handleSort, practiceType)
  const sortedUsersCount = sortedUsers?.length
  const totalPages = Math.ceil(users?.length / itemsPerPage)
  const usersCount = users?.length

  if (practiceType === PRACTICE_TYPES.STUDENT_ACTIVITY) {
    if (!isOptedInToStudentActivities && !userHasCompletedAnActivity) {
      emptyText = studentActivitiesEmptyText
      showTable = false
      showDescription = false
    }
  } else {
    if (!userHasCompletedAnActivity) {
      showTable = false
    }
  }

  if (error) return null
  if (isFetching || !users) {
    return (
      <ContentPanelWrapper>
        <LoadingWrapper>
          <SpinnerAnimatedIcon />
          <p>{LOADING_TEXT}</p>
        </LoadingWrapper>
      </ContentPanelWrapper>
    )
  }
  if (!isSetup) {
    return (
      <NotSetupWrapper>
        <EmptyMessage text={notSetupText} />
      </NotSetupWrapper>
    )
  }
  if (!userHasCompletedAnActivity) {
    return (
      <NotSetupWrapper>
        <EmptyMessage text={emptyText} />
      </NotSetupWrapper>
    )
  }
  return (
    <Fragment key="user-progress-list">
      <StyledSortableAccordionList
        currentItemCount={sortedUsersCount}
        currentPacingName={currentPacingName}
        currentPage={pageViewNumber}
        handlePageChange={handlePageChange}
        isFetching={isFetching}
        itemLabel="users"
        items={sortedUsers}
        itemsPerPage={itemsPerPage}
        lastUpdatedAt={lastUpdatedAt}
        renderEmptyMessage={() => <EmptyMessage text={emptyText} />}
        renderLastUpdatedMessage
        renderLeftBox={() => (
          <LeftBox
            description={description}
            showDescription={showDescription}
            title={title}
            usersCount={usersCount}
          />
        )}
        renderSortButton={props => (
          <AdminSortButton
            {...props}
            monitorView
            pathwayDatesExist={pathwayDatesExist}
            programKey={PROGRAM_KEYS.HS_MONITOR}
          />
        )}
        showDescription={showDescription}
        showExportButton={false}
        showHairline
        showTable={showTable}
        sortButtons={sortButtons}
        sortKey={sortKey}
        sortOrder={sortOrder}
        totalItems={usersCount}
        totalPages={totalPages}
      >
        {(user, index) => {
          const props = {
            index,
            user,
          }
          return (
            <UserProgressRow
              {...props}
              practiceType={practiceType}
              siteId={siteId}
            />
          )
        }}
      </StyledSortableAccordionList>
    </Fragment>
  )
}

const mapStateToProps = (state, ownProps) => {
  const {
    reportsManager: {
      admin: {
        hs: {
          isFetching,
          userProgress: {
            pagination: { sortKey, sortOrder },
          },
        },
        lastUpdatedAt,
      },
      error,
    },
  } = state
  const adminHsSites = licenseSelectors.selectActiveAdminHsSites(state)
  const { pacing: highSchool } = adminHsSites.find(
    site => site.id === ownProps.siteId,
  )
  const { currentPacingName } = getCurrentPacingEndDateAndName(
    highSchool.highSchool,
  )
  const pathwayDates = getPathwayDates(highSchool.highSchool)
  const pathwayDatesExist =
    Object.keys(pathwayDates || {}).length > 0
  const { isOptedInToStudentActivities, siteId, users } =
    reportsSelectors.selectHsUserProgressSnapshot(
      state,
      ownProps.siteId,
      ownProps.practiceTypeData.practiceType,
    ) || {}

  return {
    adminSortKey: sortKey,
    adminSortOrder: sortOrder,
    currentPacingName,
    error,
    isFetching,
    isOptedInToStudentActivities,
    lastUpdatedAt: lastUpdatedAt?.last_updated_at,
    pathwayDatesExist,
    userProgressSiteId: siteId,
    users,
  }
}

const mapDispatchToProps = {
  fetchHsUserProgressSnapshot: reportsOperations.fetchHsUserProgressSnapshot,
  updateHsUserProgressPaginationSortKey:
    reportsOperations.updateHsUserProgressPaginationSortKey,
  updateHsUserProgressPaginationSortOrder:
    reportsOperations.updateHsUserProgressPaginationSortOrder,
}

UserProgressList.propTypes = {
  adminSortKey: PropTypes.string,
  adminSortOrder: PropTypes.string,
  currentPacingName: PropTypes.string,
  error: PropTypes.object,
  fetchHsUserProgressSnapshot: PropTypes.func,
  isFetching: PropTypes.bool,
  isOptedInToStudentActivities: PropTypes.bool,
  isSetup: PropTypes.bool,
  lastUpdatedAt: PropTypes.string,
  pathwayDatesExist: PropTypes.bool,
  practiceTypeData: PropTypes.object,
  siteId: PropTypes.number,
  updateHsUserProgressPaginationSortKey: PropTypes.func,
  updateHsUserProgressPaginationSortOrder: PropTypes.func,
  userProgressSiteId: PropTypes.number,
  users: PropTypes.array,
}

export default connect(mapStateToProps, mapDispatchToProps)(UserProgressList)
